import React, { useCallback, useEffect, useRef, useState } from "react";
import { ordersApi } from "../services/orders";
import { paymentsApi } from "../services/payments";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import type { PaymentRequest as StripePaymentRequest } from "@stripe/stripe-js";
import InlinePayOptions from "./InlinePayOptions";
import BoostBidModal from "./BoostBidModal";
import ThreeStepIndicator from "./ThreeStepIndicator";
import "./ThreeStepIndicator.css";
import SetlistArt from "./SetlistArt";
import SetlistSealedArt from "./SetlistSealedArt";
import SetlistCountdownRing from "./SetlistCountdownRing";
import FanBoostSheet from "./FanBoostSheet";
import ClaimSheet from "./ClaimSheet";
import { PUSH_SUPPORTED, subscribeFanPush } from "../lib/pushClient";
import { roundPriceStep } from "../utils/roundPriceStep";
import { formatSingleAmount } from "../utils/currency";
import { maxBoostFor } from "../utils/payinConfig";
import { calculateFeeBreakdown, round2 } from "../utils/feeCalculator";
import {
  FanOrderRow,
  canMoneyBoost,
  canPointsBoost,
  claimOpen,
  counterLive as counterLiveFn,
  isAcceptedLike,
  isOpen,
} from "../lib/fanRequestsModel";
import "./FanLiveCard.css";
import { normTier } from "../utils/orderTier";
import { IS_NATIVE } from "../lib/stripeFlow";
import { t as tr } from "../i18n";

// 🎟️ The LIVE CARD — the request a fan is watching, expanded in place on My
// Requests (founder pick 1A: no separate confirmation page).
//
// This is the old /success card's body moved into the list: the 3 stages
// (or the setlist countdown ring), the money boost (wallet-direct Apple Pay
// or the inline two-step), the counter-offer accept / pass, the failed-
// payment retry, the notify-me email + push bell, and the one-time confetti.
// Every Stripe flow below is the SuccessPage code (punch-list items, Bug C)
// carried over line for line — only `setOrder` became `onUpdate`, because
// the PANEL owns the rows and the one poll that refreshes them.
//
// Buttons are the founder's Polymarket slab pair (C4 final, re-locked
// round 3): green Boost with the bolt, slate Request Another Song.

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const isSecure = window.location.protocol === "https:" || window.location.hostname === "localhost";
const stripePromise = STRIPE_PK && isSecure ? loadStripe(STRIPE_PK) : null;

const BOLT = "M13.2 2 5 13.4h5l-1.6 8.6L17 10.4h-5.2Z";

/** What the landing resolver (FanRequestsPanel) learned about a Stripe redirect. */
export type LandingOutcome = {
  /** The order's OWN payment failed — retry card, no polling behind it. */
  paymentFailed?: boolean;
  /** A SECONDARY payment (boost / counter-accept) failed; the order is still live. */
  softFail?: boolean;
};

type Props = {
  row: FanOrderRow;
  /** Present only on the request the fan just arrived from. */
  landing?: LandingOutcome | null;
  /** Green ring: the fresh request when a counter-offer took the top slot. */
  isNew?: boolean;
  /** A Boost chip on the compact row expanded this card — open the sheet at once. */
  autoOpenBoost?: boolean;
  signedIn: boolean;
  points: number;
  boostCost: number;
  /** Fresh server truth for this order — the panel merges it into the row. */
  onUpdate: (patch: Partial<FanOrderRow> & { orderId: string }) => void;
  onPointsBoosted: (orderId: string, newBalance: number) => void;
  /** Re-read the list from the server (a points boost the server refused). */
  onRefresh: () => void;
  onCollapse: () => void;
};

type DeclineReason = "pas_ambience" | "no_access" | "already_played" | "more_money" | "not_sharing_setlist";

const getDeclineReasonLabel = (r?: string | null): string | null => {
  switch (r as DeclineReason | undefined) {
    case "pas_ambience":
      return tr("fanLiveCard.notTheVibe");
    case "no_access":
      return tr("fanLiveCard.theArtistDoesnT");
    case "already_played":
      return tr("fanLiveCard.thisSongWasAlready");
    case "not_sharing_setlist":
      // Playlist-sale decline (Sell Playlists) — founder-owned wording.
      return tr("fanLiveCard.sorryIAmNot");
    default:
      return null;
  }
};

const FanLiveCard: React.FC<Props> = ({
  row: order,
  landing,
  isNew,
  autoOpenBoost,
  signedIn,
  points,
  boostCost,
  onUpdate,
  onPointsBoosted,
  onRefresh,
  onCollapse,
}) => {
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [showPayInline, setShowPayInline] = useState(false);
  const [piClientSecret, setPiClientSecret] = useState<string | null>(null);
  const [showCardForm, setShowCardForm] = useState(false);
  const [emailForLink, setEmailForLink] = useState("");
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const inlinePayRef = useRef<HTMLDivElement | null>(null);
  const [inlineMode, setInlineMode] = useState<null | "priority" | "counter">(null);
  // What the boost being paid costs — its intent is priced for the NEW bid.
  // The in-app Apple Pay sheet showed the order's current total (2026-10-10).
  const [boostTotal, setBoostTotal] = useState<number | null>(null);

  const [showConfetti, setShowConfetti] = useState(false);
  const [showBoostSheet, setShowBoostSheet] = useState(!!autoOpenBoost);
  // "Wasn't played?" (founder, 2026-10-09) — the sheet owns the whole claim.
  const [showClaimSheet, setShowClaimSheet] = useState(false);
  // "Other" amount → the number-pad modal (Bug C flow, prefilled at 2x).
  const [showBoostModal, setShowBoostModal] = useState(false);
  // Item 9 — a Stripe redirect landed here with a failed/canceled PI.
  const [paymentFailed, setPaymentFailed] = useState(!!landing?.paymentFailed);
  const [softFail, setSoftFail] = useState(!!landing?.softFail);
  // A live counter vanished under the fan (withdrawn / aged out).
  const [counterGone, setCounterGone] = useState(false);
  const [passBusy, setPassBusy] = useState(false);

  useEffect(() => {
    if (landing?.paymentFailed) setPaymentFailed(true);
    if (landing?.softFail) setSoftFail(true);
  }, [landing]);

  // Fan email capture (Phase 3) — COLLAPSED by default (founder decision).
  const [updatesEmail, setUpdatesEmail] = useState("");
  const [updatesEmailOpen, setUpdatesEmailOpen] = useState(false);
  const [updatesEmailBusy, setUpdatesEmailBusy] = useState(false);
  const [updatesEmailSaved, setUpdatesEmailSaved] = useState(false);
  const submitUpdatesEmail = async () => {
    if (updatesEmailBusy) return;
    setUpdatesEmailBusy(true);
    try {
      await ordersApi.setUpdatesEmail(order.orderId, updatesEmail.trim());
      setUpdatesEmailSaved(true);
    } catch {
      // Quiet failure — the field stays and they can retry; updates are a
      // bonus, never an obstacle.
    }
    setUpdatesEmailBusy(false);
  };

  // Fan web push (Phase 3c) — the 🔔 opt-in. iOS Safari has no PushManager,
  // so PUSH_SUPPORTED is false there and the button never exists.
  const [bellBusy, setBellBusy] = useState(false);
  const [bellOn, setBellOn] = useState<boolean>(() => {
    try {
      return !!localStorage.getItem(`rq_push_${order.orderId}`);
    } catch {
      return false;
    }
  });
  const enableBellPush = async () => {
    if (bellBusy) return;
    setBellBusy(true);
    try {
      const ok = await subscribeFanPush(order.orderId);
      if (ok) {
        setBellOn(true);
        try {
          localStorage.setItem(`rq_push_${order.orderId}`, "1");
        } catch {}
      }
    } catch {
      // Quiet — permission denied or a network blip; the button stays.
    }
    setBellBusy(false);
  };

  /* ── confetti ────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!showConfetti) return;
    const t = setTimeout(() => setShowConfetti(false), 7000);
    return () => clearTimeout(t);
  }, [showConfetti]);

  // Item 9 — celebrate ONLY when the order is genuinely paid, once per order.
  // A declined order can still carry paid:true (card authorized, then
  // released), so the status check must ALWAYS apply (founder catch).
  const maybeCelebrate = useCallback((o: FanOrderRow, key: string) => {
    if (["declined", "frozen", "expired", "refunded"].includes(o.status)) return;
    if (typeof o.paid === "boolean" && !o.paid) return;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {}
    setShowConfetti(true);
  }, []);

  // Landing: the payment just went through (same moment /success used to fire).
  useEffect(() => {
    if (!landing || landing.paymentFailed) return;
    maybeCelebrate(order, `rq_confetti_${order.orderId}`);
    // Once per landing per order: the order object changes on every poll and
    // maybeCelebrate is recreated per render — neither should re-fire this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.orderId, landing]);

  // And again the moment the DJ says yes while the fan is watching.
  const prevStatusRef = useRef(order.status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = order.status;
    if (prev !== order.status && !isAcceptedLike({ status: prev } as FanOrderRow) && isAcceptedLike(order)) {
      maybeCelebrate(order, `rq_confetti_acc_${order.orderId}`);
    }
  }, [order, maybeCelebrate]);

  // A live counter that turned into a plain decline under the fan — the poll
  // (panel) hands us the new row; catch the flip here.
  const prevCounterRef = useRef(counterLiveFn(order));
  useEffect(() => {
    const was = prevCounterRef.current;
    const now = counterLiveFn(order);
    prevCounterRef.current = now;
    if (was && !now && order.status === "declined") setCounterGone(true);
  }, [order]);

  /* ── wallet-direct boost ────────────────────────────────────────────── */
  const armedWalletRef = useRef<{ pr: StripePaymentRequest; amount: number; applePay: boolean } | null>(null);
  const quoteSeqRef = useRef(0);
  const [walletMarkAmount, setWalletMarkAmount] = useState<number | null>(null);
  const orderIdRef = useRef<string>(order.orderId);
  useEffect(() => {
    orderIdRef.current = order.orderId;
  }, [order.orderId]);

  const handleBoostQuote = useCallback((amount: number | null) => {
    const seq = ++quoteSeqRef.current; // stales every in-flight arm attempt
    if (amount === null) {
      armedWalletRef.current = null;
      setWalletMarkAmount(null);
      return;
    }
    const orderId = orderIdRef.current;
    // Never inside the app: the web wallet sheet cannot open in the web view
    // (see InlinePayOptions); the boost takes the inline pay path there.
    if (!orderId || !stripePromise || IS_NATIVE) return;
    (async () => {
      try {
        const data = await paymentsApi.boostQuote(orderId, amount);
        if (seq !== quoteSeqRef.current) return;
        const total = { label: "Request App", amount: data.totalCents };
        const existing = armedWalletRef.current;
        if (existing) {
          existing.pr.update({ total });
          existing.amount = amount;
          setWalletMarkAmount(existing.applePay ? amount : null);
          return;
        }
        const stripe = await stripePromise;
        if (!stripe || seq !== quoteSeqRef.current) return;
        const pr = stripe.paymentRequest({
          country: "US",
          currency: String(data.currency || "usd").toLowerCase(),
          total,
          disableWallets: ["link"],
        });
        const avail = await pr.canMakePayment();
        if (seq !== quoteSeqRef.current) return;
        if (!avail) return;
        armedWalletRef.current = { pr, amount, applePay: !!avail.applePay };
        setWalletMarkAmount(avail.applePay ? amount : null);
      } catch {
        if (seq === quoteSeqRef.current) {
          armedWalletRef.current = null;
          setWalletMarkAmount(null);
        }
      }
    })();
  }, []);

  // Reveal the two-step inline pay UI (wallet buttons + card form) for a
  // boost PI. Shared by the classic flow AND every wallet-path fallback.
  // `bid` is the new bid the PI was priced for (the server's newAmount).
  const revealBoostTwoStep = (clientSecret: string, bid: number) => {
    setPiClientSecret(clientSecret);
    setBoostTotal(calculateFeeBreakdown(bid, order.currency).customerTotal);
    setShowPayInline(true);
    setInlineMode("priority");
    setShowCardForm(false);
    setPayError(null);
    setTimeout(() => {
      inlinePayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const boostIntentFailed = (err: any) => {
    console.error("boost-intent error:", err);
    setErrorMsg(err?.response?.data?.msg || tr("fanLiveCard.failedToStartBoost"));
  };

  const startBoost = async (newAmount: number) => {
    if (!stripePromise) {
      setErrorMsg(tr("fanLiveCard.stripePublishableKeyIs"));
      return;
    }
    try {
      const data = await paymentsApi.boostIntent(order.orderId, newAmount);
      revealBoostTwoStep(data.clientSecret, data.newAmount ?? newAmount);
    } catch (err: any) {
      boostIntentFailed(err);
    }
  };

  const handlePrioritySuccess = async (piId?: string) => {
    try {
      const res = await paymentsApi.confirmIntent(order.orderId, piId);
      onUpdate({ ...res, orderId: order.orderId });
      setShowPayInline(false);
      setShowCardForm(false);
      setPayError(null);
      setInlineMode(null);
      setPiClientSecret(null);
    } catch (e: any) {
      console.error("confirm after priority/counter error:", e);
      // 409 "This counter offer is no longer available." — the DJ withdrew at
      // the exact moment the fan paid; the server already CANCELLED the hold.
      // msgEn: the server's English (the reply's msg may be translated).
      const msg = String(e?.response?.data?.msgEn ?? e?.response?.data?.msg ?? "").toLowerCase();
      if (e?.response?.status === 409 && msg.includes("counter")) {
        await resyncAfterCounterGone();
      }
    }
  };

  const walletBoost = (
    newAmount: number,
    armed: { pr: StripePaymentRequest; amount: number; applePay: boolean }
  ) => {
    let shown = false;
    try {
      armed.pr.show();
      shown = true;
    } catch (e) {
      console.error("wallet show() failed:", e);
    }
    if (!shown) {
      void startBoost(newAmount);
      return;
    }
    const orderId = orderIdRef.current;
    const intentPromise = paymentsApi.boostIntent(orderId, newAmount);
    intentPromise.catch(() => {});

    let cancelled = false;
    let succeeded = false;
    let uiSettled = false;

    armed.pr.on("cancel", async () => {
      cancelled = true;
      if (succeeded || uiSettled) return;
      try {
        const data = await intentPromise;
        if (uiSettled) return;
        uiSettled = true;
        revealBoostTwoStep(data.clientSecret, data.newAmount ?? newAmount);
      } catch (err: any) {
        if (uiSettled) return;
        uiSettled = true;
        boostIntentFailed(err);
      }
    });

    armed.pr.on("paymentmethod", async (ev) => {
      let clientSecret: string;
      let bid: number;
      try {
        const data = await intentPromise;
        clientSecret = data.clientSecret;
        bid = data.newAmount ?? newAmount;
      } catch (err: any) {
        try { ev.complete("fail"); } catch {}
        if (!uiSettled) {
          uiSettled = true;
          boostIntentFailed(err);
        }
        return;
      }
      if (cancelled) return;
      if (!stripePromise) {
        try { ev.complete("fail"); } catch {}
        return;
      }
      const stripe = await stripePromise;
      if (!stripe || cancelled) {
        try { ev.complete("fail"); } catch {}
        return;
      }
      try {
        const result = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );
        if (result.error) {
          try { ev.complete("fail"); } catch {}
          if (!uiSettled) {
            uiSettled = true;
            revealBoostTwoStep(clientSecret, bid);
            setPayError(result.error.message || tr("fanLiveCard.paymentError"));
          }
          return;
        }
        succeeded = true;
        uiSettled = true;
        try { ev.complete("success"); } catch {}
        let pi = result.paymentIntent;
        if (pi && pi.status === "requires_action") {
          const next = await stripe.confirmCardPayment(clientSecret);
          if (next.error || !next.paymentIntent) {
            succeeded = false;
            revealBoostTwoStep(clientSecret, bid);
            setPayError(next.error?.message || tr("fanLiveCard.paymentError"));
            return;
          }
          pi = next.paymentIntent;
        }
        await handlePrioritySuccess(pi?.id);
      } catch {
        try { ev.complete("fail"); } catch {}
        if (!succeeded && !uiSettled) {
          uiSettled = true;
          revealBoostTwoStep(clientSecret, bid);
          setPayError(tr("fanLiveCard.paymentError"));
        }
      }
    });
  };

  const handleBoostSubmit = async (newAmount: number) => {
    setShowBoostSheet(false);
    setShowBoostModal(false);
    // Armed wallet for exactly this amount: open the sheet DIRECTLY in this
    // tap (pr.show() before any await — Safari drops the gesture otherwise).
    const armed = armedWalletRef.current;
    if (armed && armed.amount === newAmount) {
      armedWalletRef.current = null;
      setWalletMarkAmount(null);
      walletBoost(newAmount, armed);
      return;
    }
    await startBoost(newAmount);
  };

  /* ── counter-offer ───────────────────────────────────────────────────── */
  const resyncAfterCounterGone = async () => {
    try {
      const res = await ordersApi.status(order.orderId);
      onUpdate({ ...res, orderId: order.orderId });
      setCounterGone(true);
      setShowPayInline(false);
      setShowCardForm(false);
      setPayError(null);
      setInlineMode(null);
      setPiClientSecret(null);
      return true;
    } catch {
      return false;
    }
  };

  const handlePassCounter = async () => {
    if (passBusy) return;
    setPassBusy(true);
    try {
      await ordersApi.passCounter(order.orderId);
    } catch (err: any) {
      console.error("pass-counter error:", err?.response?.data || err?.message);
    }
    try {
      const res = await ordersApi.status(order.orderId);
      onUpdate({ ...res, orderId: order.orderId });
    } catch {
      /* offline — the card stays as-is, the button re-enables for a retry */
    }
    setPassBusy(false);
  };

  const handleAcceptCounter = async () => {
    if (!stripePromise) {
      setErrorMsg(tr("fanLiveCard.stripePublishableKeyIs"));
      return;
    }
    try {
      const data = await paymentsApi.createCounterIntent(order.orderId);
      setPiClientSecret(data.clientSecret);
      setShowPayInline(true);
      setInlineMode("counter");
      setShowCardForm(false);
      setPayError(null);
      setTimeout(() => {
        inlinePayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err: any) {
      console.error("Counter intent error:", err);
      // 400 = "No valid counter offer on this order" — withdrawn or resolved
      // between render and tap. Recover silently.
      if (err?.response?.status === 400 && (await resyncAfterCounterGone())) return;
      setErrorMsg(tr("fanLiveCard.failedToStartCounter"));
    }
  };

  /* ── derived ─────────────────────────────────────────────────────────── */
  const {
    searchQuery: rawSearchQuery,
    amount,
    currency,
    trackImage,
    trackImages,
    trackArtists = [],
    status,
    declineReason,
    counterAmount,
    djSlug,
  } = order;

  // Tonight's-set orders are stored with the generic title "Setlist" — the
  // fan-facing card says "Tonight's Setlist" while it's pending.
  const searchQuery =
    order.isPlaylist && !order.playlistDeliveredAt && rawSearchQuery === "Setlist"
      ? tr("fanLiveCard.tonightSSetlist")
      : rawSearchQuery;

  const money = (n: number) => formatSingleAmount(n, currency);
  const baseRaw = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  const baseShown = roundPriceStep(baseRaw, currency);
  const total = baseShown > 0 ? calculateFeeBreakdown(baseShown, currency).customerTotal : 0;
  const counterBase = Number.isFinite(Number(counterAmount)) ? Number(counterAmount) : 0;
  const counterTotal = counterBase > 0 ? calculateFeeBreakdown(counterBase, currency).customerTotal : 0;

  const isCounter = counterLiveFn(order);
  const reasonLabel = getDeclineReasonLabel(declineReason);
  const acceptedLike = isAcceptedLike(order);
  const increaseCount = Number(order.increaseCount || 0);
  const isFirstBoost = increaseCount === 0;
  const wasBoosted =
    typeof order.originalAmount === "number" && order.originalAmount > 0 && order.originalAmount !== baseRaw;
  const boostedFromTotal = wasBoosted
    ? calculateFeeBreakdown(roundPriceStep(order.originalAmount || 0, currency), currency).customerTotal
    : 0;
  const showFailedState = paymentFailed || status === "frozen";
  const open = isOpen(order);
  const boostDoors =
    open && order.djAccepting !== false && (canMoneyBoost(order) || canPointsBoost(order, { signedIn, points, boostCost }));
  // "Wasn't played?" (founder, 2026-10-09): the door only on a paid played
  // request the server says is still claimable; once claimed, the card waits
  // on the talent's set list instead of showing the three steps.
  const checking = claimOpen(order);
  const claimDj = order.djName || "DJ";
  const claimDoor = status === "played" && order.claimable === true && !order.claim && baseRaw > 0;
  const requestUrl = djSlug ? `/dj/${djSlug}/request?prev_order_id=${order.orderId}` : null;
  const goRequestAnother = () => {
    if (requestUrl) window.location.href = requestUrl;
    else window.history.back();
  };

  // State-driven headline (order of checks matters: failed, counter, plain
  // decline, closed, accepted-like, seen, waiting).
  type Tone = "green" | "amber" | "red" | "grey";
  let headline: string;
  let tone: Tone;
  if (showFailedState) {
    headline = tr("fanLiveCard.paymentDidnTGo");
    tone = "red";
  } else if (isCounter) {
    headline = `${order.djName || "The artist"} raised the stakes`;
    tone = "amber";
  } else if (status === "declined") {
    headline = tr("fanLiveCard.notThisTime");
    tone = "red";
  } else if (status === "expired") {
    headline = order.isPlaylist ? tr("fanLiveCard.notDeliveredNeverCharged") : baseRaw > 0 ? tr("fanLiveCard.noReplyNotCharged") : tr("fanLiveCard.noReply");
    tone = "grey";
  } else if (status === "refunded") {
    headline = tr("fanLiveCard.refunded");
    tone = "grey";
  } else if (checking) {
    headline = tr("fanLiveCard.checkingWith", { djName: claimDj });
    tone = "grey";
  } else if (status === "played") {
    headline = tr("fanLiveCard.played");
    tone = "green";
  } else if (acceptedLike) {
    headline = tr("fanLiveCard.accepted");
    tone = "green";
  } else if (order.djAccepting !== false && open) {
    headline = tr("fanLiveCard.liveIsTakingRequests", { djName: order.djName || "the artist" });
    tone = "green";
  } else if (order.seenByDjAt) {
    headline = tr("fanLiveCard.seenAwaitingReply");
    tone = "grey";
  } else {
    headline = tr("fanLiveCard.waitingForAReply");
    tone = "grey";
  }

  const art = (
    <div className="flc-art">
      {order.isPlaylist && !order.playlistDeliveredAt ? (
        <SetlistSealedArt djName={order.djName} djAvatarUrl={order.djAvatarUrl} withMarks />
      ) : trackImages && trackImages.length ? (
        <SetlistArt images={trackImages} className="flc-art-img" alt={searchQuery} />
      ) : trackImage ? (
        <img src={trackImage} alt={searchQuery} className="flc-art-img" />
      ) : (
        <div className="flc-art-ph" aria-hidden="true">♪</div>
      )}
    </div>
  );

  const inlinePay = (mode: "priority" | "counter", totalToCharge: number) => (
    <div ref={inlinePayRef} className="flc-inline" style={{ display: showPayInline && inlineMode === mode ? undefined : "none" }}>
      {showPayInline && stripePromise && piClientSecret && inlineMode === mode && (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: piClientSecret, appearance: { theme: "night" }, locale: "en" }}
        >
          <InlinePayOptions
            orderId={order.orderId}
            context="song"
            djName={order.djName}
            clientSecret={piClientSecret}
            currency={order.currency}
            emailForLink={emailForLink}
            setEmailForLink={setEmailForLink}
            showCardForm={showCardForm}
            setShowCardForm={setShowCardForm}
            processing={processing}
            setProcessing={setProcessing}
            payError={payError}
            setPayError={setPayError}
            selectedTrack={null}
            totalToCharge={totalToCharge}
            onPaymentSuccess={handlePrioritySuccess}
          />
        </Elements>
      )}
    </div>
  );

  const slabRequestAnother = (
    <button type="button" className="flc-slab flc-slab--slate" onClick={goRequestAnother}>
      {tr("fanLiveCard.requestAnotherSong")}
    </button>
  );

  return (
    <div
      className={`flc flc--${tone}${isCounter ? " flc--counter" : ""}${isNew ? " flc--new" : ""}${
        order.fanBoost && (status === "pending" || acceptedLike) ? " flc--fanboost" : ""
      }`}
    >
      {showConfetti && (
        // B6 "Depth burst" (founder pick): three depth layers in brand
        // greens + white — big blurred near pieces, crisp mid, tiny far.
        <div className="confetti-container" aria-hidden>
          {Array.from({ length: 28 }).map((_, i) => {
            const layer = i < 4 ? "near" : i < 15 ? "mid" : "far";
            const duration =
              layer === "near" ? 2.6 + Math.random() * 0.6 : layer === "mid" ? 3.4 + Math.random() * 0.8 : 4.4 + Math.random() * 0.8;
            return (
              <div
                key={i}
                className={`confetti-piece confetti-piece--${layer}`}
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 1.8}s`,
                  animationDuration: `${duration}s`,
                  backgroundColor: ["#24C961", "#35e07c", "#ffffff", "#c3fae8", "#9ae6b4", "#2fdd74"][i % 6],
                }}
              />
            );
          })}
        </div>
      )}

      <button type="button" className="flc-hd" onClick={onCollapse} aria-label={tr("fanLiveCard.collapse")}>
        <span className="flc-dot" aria-hidden="true" />
        <span className="flc-hd-txt">{headline}</span>
        <span className="flc-chev" aria-hidden="true">⌃</span>
      </button>

      <div className="flc-top">
        {art}
        <div className="flc-meta">
          <div className="flc-title">{searchQuery || "—"}</div>
          {trackArtists.length > 0 && <div className="flc-artists">{trackArtists.join(", ")}</div>}
          {isCounter ? (
            <div className="flc-amt flc-amt--muted">{tr("fanLiveCard.youOffered", { amount: money(total) })}</div>
          ) : baseRaw > 0 ? (
            <div className="flc-amt">{tr("fanLiveCard.requestedFor", { amount: money(total) })}</div>
          ) : (
            <div className="flc-amt flc-amt--muted">{tr("fanLiveCard.freeRequest")}</div>
          )}
          {wasBoosted && !isCounter && <div className="flc-boosted-from">{tr("fanLiveCard.boostedFrom", { amount: money(boostedFromTotal) })}</div>}
        </div>
      </div>

      {errorMsg && <div className="flc-err">{errorMsg}</div>}

      {showFailedState ? (
        <>
          <div className="flc-fail" role="alert">
            <div className="flc-fail-t">{tr("fanLiveCard.thatPaymentDidnT")}</div>
            <div className="flc-fail-s">{tr("fanLiveCard.youHavenTBeen")}</div>
          </div>
          <div className="flc-btns">
            <button type="button" className="flc-slab flc-slab--green" onClick={goRequestAnother}>
              <span className="flc-l1">{tr("fanLiveCard.tryAgain")}</span>
            </button>
          </div>
        </>
      ) : isCounter ? (
        <>
          {softFail && (
            <div className="flc-fail" role="alert">
              <div className="flc-fail-t">{tr("fanLiveCard.thatPaymentDidnT")}</div>
              <div className="flc-fail-s">{tr("fanLiveCard.youHavenTBeen2")}</div>
            </div>
          )}
          {reasonLabel && <div className="flc-reason">“{reasonLabel}”</div>}
          <div className="flc-price">
            <span className="flc-price-l">{tr("fanLiveCard.newPrice")}</span>
            <span className="flc-price-v">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={BOLT} /></svg>
              {money(counterTotal)}
            </span>
          </div>
          <div className="flc-btns">
            <button type="button" className="flc-slab flc-slab--green" onClick={handleAcceptCounter}>
              <span className="flc-l1wrap">
                <svg className="flc-bolt" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={BOLT} /></svg>
                <span className="flc-l1">{tr("fanLiveCard.yesPlayIt")}</span>
              </span>
            </button>
            {inlinePay("counter", counterTotal)}
            <button type="button" className="flc-slab flc-slab--slate" onClick={handlePassCounter} disabled={passBusy}>
              {passBusy ? tr("fanLiveCard.oneSec") : tr("fanLiveCard.noThanks")}
            </button>
            {slabRequestAnother}
          </div>
        </>
      ) : (
        <>
          {softFail && (
            <div className="flc-fail" role="alert">
              <div className="flc-fail-t">{tr("fanLiveCard.thatPaymentDidnT")}</div>
              <div className="flc-fail-s">{tr("fanLiveCard.noExtraChargeWas")}</div>
            </div>
          )}
          {counterGone && (
            <div className="flc-fail" role="alert">
              <div className="flc-fail-t">{tr("fanLiveCard.thisCounterOfferIs")}</div>
              <div className="flc-fail-s">{tr("fanLiveCard.youHavenTBeen3")}</div>
            </div>
          )}

          {order.isPlaylist && !order.playlistDeliveredAt && ["pending", "queued", "accepted"].includes(status) ? (
            // A setlist has no "played at the party" arc — the fan is waiting
            // on a delivery. The 72h ring counts from paidAt like the sweep.
            <SetlistCountdownRing paidAt={order.paidAt} createdAt={order.createdAt} />
          ) : order.isPlaylist && status === "expired" ? (
            <div className="flc-note">
              {tr("fanLiveCard.theDjDidnT")}
            </div>
          ) : status === "expired" ? (
            <div className="flc-note">
              {baseRaw > 0
                ? tr("fanLiveCard.theDjDidnT2")
                : tr("fanLiveCard.theDjDidnT3")}
            </div>
          ) : status === "refunded" ? (
            <div className="flc-note">{tr("fanLiveCard.theDjRefundedThis")}</div>
          ) : checking ? (
            <div className="flc-note">{tr("fanLiveCard.youLlHearBack")}</div>
          ) : (
            <div className={normTier(order.tier) === "fast" ? "is-fast" : ""}>
              <ThreeStepIndicator
                status={status as any}
                isPriority={normTier(order.tier) === "fast"}
                seenByDjAt={order.seenByDjAt || null}
                isCountered={isCounter}
              />
            </div>
          )}
          {/* The set list showed the song: still played. No time — we don't know it. */}
          {status === "played" && order.claim?.status === "rejected" && (
            <div className="flc-note">{tr("fanLiveCard.itSOnSetList", { djName: claimDj })}</div>
          )}

          {/* Fan email capture (Phase 3) — SILENT policy: only typed-card
              payers (no email on file), only while pending, only signed out
              (a signed-in fan's status emails go to the account address). */}
          {status === "pending" && !signedIn && !order.hasEmail && !updatesEmailSaved && !updatesEmailOpen && (
            <button type="button" className="flc-email-link" onClick={() => setUpdatesEmailOpen(true)}>
              {tr("fanLiveCard.notifyMe")}
            </button>
          )}
          {status === "pending" && !order.hasEmail && !updatesEmailSaved && updatesEmailOpen && (
            <div className="flc-email-row">
              <div className="flc-email-input-row">
                <input
                  className="flc-email-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder={tr("fanLiveCard.youEmailCom")}
                  autoFocus
                  value={updatesEmail}
                  onChange={(e) => setUpdatesEmail(e.target.value.slice(0, 320))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && updatesEmail.includes("@")) submitUpdatesEmail();
                  }}
                />
                <button
                  type="button"
                  className="flc-email-go"
                  disabled={updatesEmailBusy || !updatesEmail.includes("@")}
                  onClick={submitUpdatesEmail}
                >
                  {updatesEmailBusy ? "…" : tr("fanLiveCard.save")}
                </button>
              </div>
              <div className="flc-fine">{tr("fanLiveCard.onlyAboutThisRequest")}</div>
            </div>
          )}
          {status === "pending" && updatesEmailSaved && (
            <div className="flc-fine flc-fine--set">{tr("fanLiveCard.youReSetWe")}</div>
          )}
          {status === "pending" && PUSH_SUPPORTED && Notification.permission !== "denied" && !bellOn && (
            <button type="button" className="flc-bell" onClick={enableBellPush} disabled={bellBusy}>
              {bellBusy ? "…" : tr("fanLiveCard.turnOnNotifications")}
            </button>
          )}
          {status === "pending" && bellOn && <div className="flc-fine flc-fine--set">{tr("fanLiveCard.onWeLlPing")}</div>}

          {/* The DJ picked a reason and the decline modal promises "The fan
              sees your reason". */}
          {status === "declined" && reasonLabel && <div className="flc-reason">“{reasonLabel}”</div>}

          <div className="flc-btns">
            {boostDoors && (
              <button type="button" className="flc-slab flc-slab--green" onClick={() => setShowBoostSheet(true)}>
                <span className="flc-l1wrap">
                  <svg className="flc-bolt" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={BOLT} /></svg>
                  <span className="flc-l1">{isFirstBoost ? tr("fanLiveCard.boostThisRequest") : tr("fanLiveCard.boostAgain")}</span>
                </span>
                <span className="flc-l2">
                  {canMoneyBoost(order)
                    ? canPointsBoost(order, { signedIn, points, boostCost })
                      ? tr("fanLiveCard.doubleYourBidOr", { boostCost: boostCost.toLocaleString() })
                      : isFirstBoost
                      ? tr("fanLiveCard.doubleYourBid")
                      : tr("fanLiveCard.boostsLeft", { count: 3 - increaseCount })
                    : tr("fanLiveCard.usePoints", { boostCost: boostCost.toLocaleString() })}
                </span>
              </button>
            )}
            {inlinePay("priority", boostTotal ?? total)}
            {slabRequestAnother}
            {claimDoor && (
              <button type="button" className="flc-email-link" onClick={() => setShowClaimSheet(true)}>
                {tr("fanLiveCard.wasnTPlayed")}
              </button>
            )}
          </div>
        </>
      )}

      <BoostBidModal
        isOpen={showBoostModal}
        currentAmount={baseShown}
        currency={currency}
        capsLifted={order.djCapsLifted}
        initialAmount={isFirstBoost && baseShown > 0 ? Math.min(round2(baseShown * 2), maxBoostFor(currency, order.djCapsLifted)) : null}
        onClose={() => setShowBoostModal(false)}
        onSubmit={handleBoostSubmit}
        onQuote={handleBoostQuote}
        walletMarkAmount={walletMarkAmount}
      />

      {showBoostSheet && (
        <FanBoostSheet
          row={order}
          signedIn={signedIn}
          points={points}
          boostCost={boostCost}
          walletMarkAmount={walletMarkAmount}
          onQuote={handleBoostQuote}
          onMoney={(amt) => void handleBoostSubmit(amt)}
          onOther={() => {
            setShowBoostSheet(false);
            setShowBoostModal(true);
          }}
          onClose={() => setShowBoostSheet(false)}
          onPointsBoosted={(bal) => {
            setShowBoostSheet(false);
            onPointsBoosted(order.orderId, bal);
          }}
          onRefresh={onRefresh}
        />
      )}

      {showClaimSheet && (
        <ClaimSheet row={order} amount={money(total)} onUpdate={onUpdate} onClose={() => setShowClaimSheet(false)} />
      )}
    </div>
  );
};

export default FanLiveCard;
